import { z } from "zod";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const comicComponentKindV1Schema = z.enum([
    "BALLOON",
    "CAPTION",
    "SFX",
    "CONNECTOR",
    "MASTHEAD",
    "ISSUE_LABEL",
    "IMPRINT",
    "FOLIO",
    "FRONT_MATTER",
    "BACK_MATTER",
    "DECORATION",
    "PANEL_FRAME",
    "INSET",
    "PAGE_OPENER",
    "RECAP",
    "CAST_CARD",
]);
export const comicStructuralKindV1Schema = z.enum([
    "FRAME_STANDARD",
    "FRAME_ACTION",
    "FRAME_MEMORY",
    "INSET_RECTANGLE",
    "INSET_CIRCLE",
    "CHAPTER_OPENER",
    "RECAP_BAND",
    "CAST_CARD",
]);
export const comicStructuralTextRoleV1Schema = z.enum([
    "EYEBROW",
    "TITLE",
    "BODY",
    "NAME",
    "ROLE",
    "DETAIL",
]);
export const comicLetteringSemanticKindV1Schema = z.enum([
    "CAPTION",
    "DIALOGUE",
    "JOINED_DIALOGUE",
    "WHISPER",
    "RADIO",
    "THOUGHT",
    "MEMORY",
    "SHOUT",
    "DISTRESSED",
    "OFF_PANEL",
    "SFX",
]);
export const comicSfxKindV1Schema = z.enum([
    "IMPACT",
    "MOTION",
    "ENVIRONMENTAL",
    "MECHANICAL",
    "MAGICAL",
    "QUIET",
]);
export const comicComponentDefinitionV1Schema = z
    .object({
    version: z.literal(1),
    componentId: z.string().min(1).max(160),
    componentVersion: z.string().min(1).max(80),
    kind: comicComponentKindV1Schema,
    displayName: z.string().trim().min(1).max(160),
    semanticKinds: z.array(comicLetteringSemanticKindV1Schema).max(20),
    captionKinds: z
        .array(z.enum([
        "SCENE",
        "TIME_LOCATION",
        "INNER_MONOLOGUE",
        "SPOKEN",
        "EDITORIAL",
        "MEMORY",
        "CLOSING",
    ]))
        .max(20),
    sfxKinds: z.array(comicSfxKindV1Schema).max(20).optional(),
    structuralKinds: z.array(comicStructuralKindV1Schema).max(20).optional(),
    vectorPrimitive: z.enum([
        "ORGANIC_BALLOON",
        "CLOUD_BALLOON",
        "RADIO_BALLOON",
        "SHOUT_BALLOON",
        "DISTRESSED_BALLOON",
        "OFF_PANEL_BALLOON",
        "CLIPPED_CAPTION",
        "SPATIAL_TEXT",
        "SFX_IMPACT_TEXT",
        "SFX_MOTION_TEXT",
        "SFX_ENVIRONMENTAL_TEXT",
        "SFX_MECHANICAL_TEXT",
        "SFX_MAGICAL_TEXT",
        "SFX_QUIET_TEXT",
        "JOINED_CONNECTOR",
        "TEXT_LOCKUP",
        "BADGE",
        "RULE",
        "FOLIO_LOCKUP",
        "CREDITS_LOCKUP",
        "END_MARK",
        "SCENE_TRANSITION_MARK",
        "PANEL_FRAME_STANDARD",
        "PANEL_FRAME_ACTION",
        "PANEL_FRAME_MEMORY",
        "INSET_RECTANGLE",
        "INSET_CIRCLE",
        "CHAPTER_OPENER_LOCKUP",
        "RECAP_BAND_LOCKUP",
        "CAST_CARD_LOCKUP",
    ]),
    fontRole: z.enum(["DIALOGUE", "QUIET", "RADIO", "CAPTION", "DISPLAY"]),
    silhouette: z.enum(["ORGANIC", "CLOUD", "RADIO", "SHOUT", "DISTRESSED", "OFF_PANEL", "NONE"]),
    tail: z.enum(["TAPERED", "BUBBLE_CHAIN", "SIGNAL", "EDGE", "NONE"]),
    constraints: z
        .object({
        minimumTextPoints: z.number().min(6).max(96),
        maximumLines: z.number().int().min(1).max(20),
        minimumContrastRatio: z.number().min(1).max(21),
        leadingRatio: z.number().min(0.7).max(3),
        horizontalPaddingPoints: z.number().min(0).max(100),
        borderWidthPoints: z.number().min(0).max(20),
        opacity: z.number().min(0).max(1),
    })
        .strict(),
    accessibilityRole: z.string().trim().min(1).max(120),
    implementationId: z.string().min(1).max(200),
    implementationHashSha256: sha256Schema,
    ownership: z.literal("LIFESCAPSULE_INTERNAL_VECTOR"),
})
    .strict();
export const comicThemeV1Schema = z
    .object({
    version: z.literal(1),
    themeId: z.string().min(1).max(160),
    themeVersion: z.string().min(1).max(80),
    displayName: z.string().trim().min(1).max(160),
    designSystemVersion: z.string().min(1).max(160),
    colorTokens: z.record(z.string().min(1), z.string().regex(/^#[A-Fa-f0-9]{6}$/)),
    typography: z.record(z.string().min(1), z
        .object({
        family: z.string().min(1).max(160),
        weight: z.number().int().min(100).max(900),
        minimumPoints: z.number().min(6).max(96),
        case: z.enum(["UPPERCASE", "SENTENCE", "SOURCE"]),
        leadingRatio: z.number().min(0.7).max(3),
    })
        .strict()),
    componentIds: z.array(z.string().min(1).max(160)).min(1).max(200),
    supportedReadingDirections: z
        .array(z.enum(["LTR", "RTL"]))
        .min(1)
        .max(2),
    supportedFormats: z
        .array(z.enum(["DIGITAL", "PRINT"]))
        .min(1)
        .max(2),
})
    .strict();
export const comicComponentCatalogV1Schema = z
    .object({
    version: z.literal(1),
    catalogId: z.string().min(1).max(160),
    catalogVersion: z.string().min(1).max(80),
    catalogHashSha256: sha256Schema,
    components: z.array(comicComponentDefinitionV1Schema).min(1).max(200),
    themes: z.array(comicThemeV1Schema).min(1).max(50),
})
    .strict()
    .superRefine((catalog, context) => {
    const componentIds = catalog.components.map(({ componentId }) => componentId);
    if (new Set(componentIds).size !== componentIds.length)
        context.addIssue({
            code: "custom",
            path: ["components"],
            message: "Comic component IDs must be unique within a catalog version.",
        });
    const known = new Set(componentIds);
    for (const [themeIndex, theme] of catalog.themes.entries()) {
        if (new Set(theme.componentIds).size !== theme.componentIds.length)
            context.addIssue({
                code: "custom",
                path: ["themes", themeIndex, "componentIds"],
                message: "A Comic theme cannot bind the same component more than once.",
            });
        for (const [componentIndex, componentId] of theme.componentIds.entries())
            if (!known.has(componentId))
                context.addIssue({
                    code: "custom",
                    path: ["themes", themeIndex, "componentIds", componentIndex],
                    message: "Comic themes may reference only components in the same catalog.",
                });
    }
});
export const comicComponentUseV1Schema = z
    .object({
    version: z.literal(1),
    objectId: z.string().min(1).max(160),
    componentId: z.string().min(1).max(160),
    componentVersion: z.string().min(1).max(80),
    implementationHashSha256: sha256Schema,
    dependencyComponentIds: z.array(z.string().min(1).max(160)).max(20),
    themeId: z.string().min(1).max(160),
    themeVersion: z.string().min(1).max(80),
    semanticKind: comicLetteringSemanticKindV1Schema,
    sfxKind: comicSfxKindV1Schema.optional(),
    resolved: z
        .object({
        fontRole: z.enum(["DIALOGUE", "QUIET", "RADIO", "CAPTION", "DISPLAY"]),
        silhouette: z.enum([
            "ORGANIC",
            "CLOUD",
            "RADIO",
            "SHOUT",
            "DISTRESSED",
            "OFF_PANEL",
            "NONE",
        ]),
        tail: z.enum(["TAPERED", "BUBBLE_CHAIN", "SIGNAL", "EDGE", "NONE"]),
        fontSizePoints: z.number().min(6).max(96),
        fillHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
        strokeHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
    })
        .strict(),
})
    .strict();
export const comicStructuralTextV1Schema = z
    .object({
    sourceId: z.string().min(1).max(240),
    role: comicStructuralTextRoleV1Schema,
    text: z.string().trim().min(1).max(1_500),
})
    .strict();
export const comicStructuralObjectV1Schema = z
    .object({
    version: z.literal(1),
    id: z.string().min(1).max(160),
    kind: comicStructuralKindV1Schema,
    box: z
        .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
    })
        .strict()
        .superRefine((box, context) => {
        if (box.x + box.width > 1 || box.y + box.height > 1)
            context.addIssue({
                code: "custom",
                message: "Structural component boxes must remain inside the page.",
            });
    }),
    panelId: z.string().min(1).max(120).optional(),
    readingOrder: z.number().int().positive().max(200),
    accessibleOrder: z.number().int().positive().max(200),
    sourceTexts: z.array(comicStructuralTextV1Schema).max(20),
    assetId: z.string().min(1).max(200).optional(),
    focusPoint: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict()
        .optional(),
    sourceWindow: z
        .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
    })
        .strict()
        .superRefine((box, context) => {
        if (box.x + box.width > 1 || box.y + box.height > 1)
            context.addIssue({
                code: "custom",
                message: "Structural source windows must remain inside the bound asset.",
            });
    })
        .optional(),
})
    .strict()
    .superRefine((object, context) => {
    const sourceIds = object.sourceTexts.map(({ sourceId }) => sourceId);
    if (new Set(sourceIds).size !== sourceIds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceTexts"],
            message: "Structural source-text IDs must be unique.",
        });
    const frame = object.kind.startsWith("FRAME_");
    if (frame && !object.panelId)
        context.addIssue({
            code: "custom",
            path: ["panelId"],
            message: "Panel frames must bind to a panel.",
        });
    if (frame && object.sourceTexts.length)
        context.addIssue({
            code: "custom",
            path: ["sourceTexts"],
            message: "Panel frames cannot carry editorial copy.",
        });
    if (object.kind.startsWith("INSET_") && !object.assetId)
        context.addIssue({
            code: "custom",
            path: ["assetId"],
            message: "Picture-in-picture insets must bind to an immutable asset ID.",
        });
    if (["CHAPTER_OPENER", "RECAP_BAND", "CAST_CARD"].includes(object.kind) &&
        !object.sourceTexts.length)
        context.addIssue({
            code: "custom",
            path: ["sourceTexts"],
            message: "Editorial structural components require source-bound copy.",
        });
});
export const comicStructuralLayerV1Schema = z
    .object({
    version: z.literal(1),
    objects: z.array(comicStructuralObjectV1Schema).max(100),
})
    .strict()
    .superRefine((layer, context) => {
    const ids = layer.objects.map(({ id }) => id);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["objects"],
            message: "Structural object IDs must be unique within a page.",
        });
    for (const field of ["readingOrder", "accessibleOrder"]) {
        const values = layer.objects.map((object) => object[field]);
        if (new Set(values).size !== values.length)
            context.addIssue({
                code: "custom",
                path: ["objects"],
                message: `${field} values must be unique within the structural layer.`,
            });
    }
});
export const comicStructuralComponentUseV1Schema = z
    .object({
    version: z.literal(1),
    objectId: z.string().min(1).max(160),
    componentId: z.string().min(1).max(160),
    componentVersion: z.string().min(1).max(80),
    implementationHashSha256: sha256Schema,
    themeId: z.string().min(1).max(160),
    themeVersion: z.string().min(1).max(80),
    structuralKind: comicStructuralKindV1Schema,
    renderedPdfPageNumber: z.number().int().positive(),
    sourceTextIds: z.array(z.string().min(1).max(240)).max(20),
    assetId: z.string().min(1).max(200).optional(),
    sourceWindow: z
        .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
    })
        .strict()
        .optional(),
    focusPoint: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict()
        .optional(),
    resolved: z
        .object({
        borderWidthPoints: z.number().min(0).max(20),
        minimumTextPoints: z.number().min(6).max(96),
        fillHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
        strokeHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
    })
        .strict(),
})
    .strict();
export const comicPublishingComponentRoleV1Schema = z.enum([
    "FOLIO",
    "CREDITS",
    "END_MARK",
    "SCENE_TRANSITION",
]);
export const comicPublishingComponentUseV1Schema = z
    .object({
    version: z.literal(1),
    instanceId: z.string().min(1).max(160),
    componentId: z.string().min(1).max(160),
    componentVersion: z.string().min(1).max(80),
    implementationHashSha256: sha256Schema,
    themeId: z.string().min(1).max(160),
    themeVersion: z.string().min(1).max(80),
    role: comicPublishingComponentRoleV1Schema,
    renderedPdfPageNumber: z.number().int().positive(),
    sourceTextIds: z.array(z.string().min(1).max(240)).max(50),
    resolved: z
        .object({
        fontRole: z.enum(["DIALOGUE", "QUIET", "RADIO", "CAPTION", "DISPLAY"]),
        fontSizePoints: z.number().min(6).max(96),
        fillHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
        strokeHex: z
            .string()
            .regex(/^#[A-Fa-f0-9]{6}$/)
            .optional(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=comic-components.js.map